/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useMemo, useCallback } from 'react'
import moment, { type Moment } from 'moment'
import { Text } from 'ic-snacks'
import { FormattedMessage } from 'react-intl'
import styled from '@emotion/styled'
import withRetailerRouter from 'legacy/common/withRouter'
import { ShiftType, type ShiftsRouteQuery } from 'domains/shifts/components/types'
import Dropdown from 'legacy/components/Dropdown'
import FilterBar from 'legacy/components/FilterBar'
import Link from 'legacy/components/Link'
import Tabs from 'legacy/components/Tabs'
import { ShiftsSingleDatePicker } from 'legacy/components/Dates/ShiftsSingleDatePicker'
import { type RetailerRouteComponentProps } from 'legacy/components/RetailerRoute'
import {
  getLocationShiftTypes,
  buildLocationDropdownOption,
  locationHasPickupAccess,
} from 'legacy/common/warehouseLocations'
import { DailyWeeklyToggle } from 'legacy/components/Dates/DailyWeeklyToggle'
import { WeekPicker } from 'legacy/components/Dates/WeekPicker'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'
import { useDomainAccessControl } from 'domains/shifts/utils/domain/accessControl'
import { Flipper } from 'domains/shifts/access-control/flippers'
import { useValidWarehouseLocations } from 'domains/shifts/utils/hooks/useValidWarehouseLocations.hook'
import AddShopperDropdown from 'domains/shifts/components/components/recommended-shifts/AddShopperDropdown'
import { type Shopper } from 'legacy/routes/shoppers/types'
import { useShiftsStateContext } from 'domains/shifts/components/contexts/ShiftsStateContext'

const AddShopperDropdownContainer = styled.div({
  width: '250px',
})

type ShiftTab = { value: ShiftType; label: string }

const SHIFT_TABS: { [Key in ShiftType]: ShiftTab } = {
  [ShiftType.Picking]: { value: ShiftType.Picking, label: 'shiftsDomain.picking' },
  [ShiftType.Running]: { value: ShiftType.Running, label: 'shiftsDomain.running' },
  [ShiftType.FullService]: { value: ShiftType.FullService, label: 'shiftsDomain.fullService' },
  [ShiftType.Driving]: { value: ShiftType.Driving, label: 'shiftsDomain.driving' },
}

const YESTERDAY = moment().subtract(1, 'day')
const LAST_SCHEDULABLE_DAY = moment().startOf('week').add(4, 'weeks')

const isOutsideRange = (date: Moment) => !date.isBetween(YESTERDAY, LAST_SCHEDULABLE_DAY)

interface Props extends RetailerRouteComponentProps<{}, ShiftsRouteQuery> {
  isWeeklyView: boolean
  handleDailyWeeklyToggle(isWeeklyView: boolean): void
  children?: React.ReactElement
  onAddShopper(shopper: Shopper): void
  excludeShopperIds: number[]
}

const ShiftFilters: React.FC<Props> = props => {
  const {
    query,
    history,
    isWeeklyView,
    handleDailyWeeklyToggle,
    excludeShopperIds,
    onAddShopper,
    children,
  } = props
  const hasAccess = useDomainAccessControl()

  const {
    shiftsState: { shiftType },
  } = useShiftsStateContext()

  const performanceOverviewDisabled = hasAccess({ flippers: Flipper.PerformanceOverviewDisabled })
  const platformOnlyEnabled = hasAccess({ flippers: Flipper.PlatformOnly })

  const warehouseContext = useWarehouseContext()

  const validLocations = useValidWarehouseLocations(warehouseContext)

  const locationOptions = useMemo(
    () => validLocations.map(n => buildLocationDropdownOption(n)),
    [validLocations]
  )

  const selectedWarehouseLocation = useMemo(
    () => validLocations.find(n => n.id === query.warehouse_location_id),
    [query.warehouse_location_id, validLocations]
  )

  const showAnalyticsCta: boolean = useMemo(() => {
    if (!selectedWarehouseLocation) {
      return false
    }

    return locationHasPickupAccess(selectedWarehouseLocation)
  }, [selectedWarehouseLocation])

  const { handleChangeActiveShiftType } = useShiftsStateContext()

  const shiftTabs: ShiftTab[] = useMemo(() => {
    if (!selectedWarehouseLocation) {
      return []
    }

    return getLocationShiftTypes(selectedWarehouseLocation, platformOnlyEnabled).map(
      n => SHIFT_TABS[n]
    )
  }, [selectedWarehouseLocation, platformOnlyEnabled])

  const handleWarehouseLocationChange = useCallback(
    (id: string) => {
      const newQuery = {
        ...query,
        warehouse_location_id: id,
      }

      // @TODO -- perhaps lift to container?
      // if warehouse_location_id isn't defined its the first page load so replace the url to not pollute history
      if (query.warehouse_location_id === undefined) {
        history.replace({ query: newQuery })
      } else {
        history.push({ query: newQuery })
      }
    },
    [query, history]
  )

  const handleDateChange = useCallback(
    (date: Moment) => {
      const formattedDate = date.format('YYYY-MM-DD')
      const newQuery = {
        ...query,
        date: formattedDate,
      }

      // if date isn't defined its the first page load so replace the url to not pollute history
      if (query.date === undefined) {
        history.replace({ query: newQuery })
      } else {
        history.push({ query: newQuery })
      }
    },
    [query, history]
  )

  const updateQueryWeek = useCallback(
    (week: string) => {
      const newQuery = {
        ...query,
        week,
      }
      // if week isn't defined its the first page load so replace the url to not pollute history
      if (query.week === undefined) {
        history.replace({ query: newQuery })
      } else {
        history.push({ query: newQuery })
      }
    },
    [query, history]
  )

  useEffect(() => {
    if (!query.date) {
      handleDateChange(moment())
    }
  }, [query.date, handleDateChange])

  useEffect(() => {
    if (!query.warehouse_location_id && validLocations.length > 0) {
      handleWarehouseLocationChange(validLocations[0].id)
    }
  }, [validLocations, query.warehouse_location_id, handleWarehouseLocationChange])

  useEffect(() => {
    if (!selectedWarehouseLocation) {
      return
    }

    const validShiftTypes = getLocationShiftTypes(selectedWarehouseLocation, platformOnlyEnabled)

    if (validShiftTypes.includes(shiftType)) {
      return
    }

    handleChangeActiveShiftType(validShiftTypes[0])
  }, [shiftType, handleChangeActiveShiftType, selectedWarehouseLocation, platformOnlyEnabled])

  return (
    <React.Fragment>
      <FilterBar style={{ borderBottom: 'none' }}>
        <FilterBar.Section>
          <Dropdown
            width="250px"
            filterable
            busy={warehouseContext.loading}
            options={locationOptions}
            value={query.warehouse_location_id}
            onChange={handleWarehouseLocationChange}
          />
          <FilterBar.Item>
            {showAnalyticsCta && performanceOverviewDisabled ? (
              <Link
                route="pickupAnalytics"
                query={{ warehouseLocationId: query.warehouse_location_id }}
              >
                <Text variant="T.14" fontWeight="semiBold" style={{ color: null }}>
                  <FormattedMessage id="shiftsDomain.cta.toPickupAnalytics" />
                </Text>
              </Link>
            ) : null}
          </FilterBar.Item>
        </FilterBar.Section>
        <FilterBar.Section>
          {isWeeklyView ? (
            <WeekPicker week={query.week} updateQueryWeek={updateQueryWeek} />
          ) : (
            <ShiftsSingleDatePicker
              date={query.date}
              onDateChange={handleDateChange}
              isOutsideRange={isOutsideRange}
              displayFormat={'dddd, MMM D'}
            />
          )}
          <DailyWeeklyToggle
            isWeeklyView={isWeeklyView}
            handleDailyWeeklyToggle={handleDailyWeeklyToggle}
          />
        </FilterBar.Section>
      </FilterBar>

      {!performanceOverviewDisabled
        ? // React.cloneElement currently assumes only a single child here
          React.cloneElement(children, {
            showAnalyticsCta,
            warehouseLocationId: query.warehouse_location_id,
          })
        : null}

      <FilterBar padding="0 20px">
        <FilterBar.Section fullBleed>
          <Tabs
            onClick={handleChangeActiveShiftType}
            selected={shiftType}
            tabs={shiftTabs}
            fillHeight
          />
        </FilterBar.Section>
        <AddShopperDropdownContainer>
          <AddShopperDropdown onAddShopper={onAddShopper} excludeShopperIds={excludeShopperIds} />
        </AddShopperDropdownContainer>
      </FilterBar>
    </React.Fragment>
  )
}

export default withRetailerRouter(ShiftFilters)
